import { useEffect } from "react";
import Link from "next/link";
import { stringify } from "qs";

import { Action, Category } from "constants/events";
import { useEventTracker } from "hooks/useEventTracker";
import { getMenuItemUrl } from "utils/menuItemUrl";

import CardFlag from "components/botanic/Card/CardFlag";
import StarRating from "components/botanic/StarRating";
import DirectShippingIcon from "components/Icons/direct-shipping.svg";
import ScooterIcon from "components/Icons/scooter.svg";
import StorefrontIcon from "components/Icons/storefront.svg";
import Image from "components/Image";
import OfferLabel from "components/OfferLabel";

import { ShopItem } from "./types";

type ShopItemCardProps = {
  shopItem: ShopItem;
};
const ShopItemCard: React.FC<ShopItemCardProps> = ({ shopItem }) => {
  const {
    brand,
    brandSlug,
    category,
    collapsedResultsSummary,
    dealActive,
    dealDiscountLabel,
    dispensaryName,
    distanceMi,
    fulfillmentMethods = [],
    imageUrl,
    name,
    price,
    originalPrice,
    offerLabel,
    amount,
    productSlug,
    reviewCount,
    reviewRating,
    strainCategory,
    strainCbd,
    strainThc,
    subcategory,
    tags = [],
    dispensarySlug,
    menuItemId,
    variantId: variant,
  } = shopItem.attributes;

  const { type } = shopItem;

  const hasDelivery = fulfillmentMethods.includes("delivery");
  const hasPickup = fulfillmentMethods.includes("pickup");
  const hasShipping = fulfillmentMethods.includes("shipping");
  const isStaffPick = !!(tags && tags[0] === "staff pick");
  const cardFlagText = tags
    ? tags[0]?.charAt(0).toUpperCase() + tags[0]?.slice(1)
    : "";

  const pdpUrl =
    type === "product"
      ? `/brands/${brandSlug}/products/${productSlug}${variant ? `?${stringify({ variant })}` : ""}`
      : dispensarySlug && menuItemId
        ? getMenuItemUrl({
            dispensarySlug: dispensarySlug,
            menuItemId: menuItemId,
            name: name,
          })
        : "";

  const variantInfo = price ? (
    <div>
      <PriceDisplay
        dealActive={dealActive}
        dealDiscountLabel={dealDiscountLabel}
        offerLabel={offerLabel}
        originalPrice={`$${originalPrice?.toFixed(2)}`}
        price={`$${price?.toFixed(2)}`}
        amount={amount}
      />
      {dispensaryName && distanceMi ? (
        <div className="text-xs font-bold line-clamp-2 overflow-hidden text-ellipsis">
          {distanceMi.toFixed(2)} mi away at {dispensaryName}
        </div>
      ) : null}
    </div>
  ) : null;

  const collapsedResultsSummaryDisplay = collapsedResultsSummary ? (
    <div className="text-xs">{collapsedResultsSummary}</div>
  ) : null;

  const fulfillmentDisplay = (
    <div className="flex gap-1 flex-wrap">
      {hasPickup && (
        <div className="flex gap-1 items-center bg-leafly-white rounded px-sm">
          <StorefrontIcon height="12" />
          <span className="text-xs font-bold">Pickup</span>
        </div>
      )}
      {hasDelivery && (
        <div className="flex gap-1 items-center bg-leafly-white rounded px-sm">
          <ScooterIcon height="12" />
          <span className="text-xs font-bold">Delivery</span>
        </div>
      )}
      {hasShipping && (
        <div className="flex gap-1 items-center bg-green-20 rounded pr-sm pl-xs">
          <DirectShippingIcon height="12" />
          <span className="text-xs font-bold">Ships directly to you</span>
        </div>
      )}
    </div>
  );

  const categoryDisplay = subcategory ? subcategory : category;

  const { publishEvent } = useEventTracker();

  useEffect(() => {
    publishEvent({
      action: Action.impression,
      category: Category.unifiedShoppingExperience,
      label:
        type === "product"
          ? "v2 product card - brand pdp"
          : "v2 product card - menu pdp",
    });
  }, []);

  return (
    <div className="relative shadow-low rounded p-lg">
      <Link
        href={pdpUrl}
        onClick={() => {
          publishEvent({
            action: Action.click,
            category: Category.unifiedShoppingExperience,
            label:
              type === "product"
                ? "v2 product card - brand pdp"
                : "v2 product card - menu pdp",
          });
        }}
        data-testid="shop-item-card-link"
      >
        <div className="grid md:grid-cols-[1fr_200px] gap-6">
          <CardFlag
            menuCardIndicators={{
              cardFlagText: cardFlagText,
              showStaffPick: isStaffPick,
            }}
            isHorizontalCard={true}
          />
          <div className="grid grid-cols-[104px_1fr] gap-3">
            <div>
              <Image
                src={imageUrl}
                alt={name}
                sizes={[116]}
                ratio="square"
                data-testid="shop-item-card__image"
                imageClass="rounded"
              />
            </div>
            <div className="flex flex-col gap-3 justify-start md:truncate">
              <div className="flex flex-col gap-2 justify-start">
                <div>
                  <div className="text-xs">{brand}</div>
                  <div className="font-bold md:truncate">{name}</div>
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex flex-col md:flex-row gap-1">
                    <div className="flex gap-1">
                      <div className="bg-leafly-white font-bold rounded px-sm text-xs">
                        {categoryDisplay}
                      </div>
                      {!!strainCategory && (
                        <div className="bg-leafly-white font-bold rounded px-sm text-xs">
                          {strainCategory}
                        </div>
                      )}
                    </div>

                    {strainThc || strainCbd ? (
                      <div className="flex gap-1">
                        {!!strainThc && (
                          <div className="text-xs uppercase">{`thc ${strainThc}%`}</div>
                        )}
                        {!!strainCbd && (
                          <div className="text-xs uppercase">{`cbd ${strainCbd}%`}</div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {reviewRating && reviewCount > 0 ? (
                    <StarRating
                      rating={reviewRating}
                      ratingCount={reviewCount}
                      showRating={false}
                      starWidth={18}
                      data-testid="menu-item-card__rating"
                    />
                  ) : null}
                </div>

                {/* On mobile screens, show all this info at the bottom of the card */}
                <div className="flex flex-col gap-2 md:hidden">
                  {variantInfo}
                  {fulfillmentDisplay}
                  {collapsedResultsSummaryDisplay}
                </div>
                {/* On medium or larger screens, show fulfillment options here at the column bottom, move price and other things to the next column (see below) */}
                <div className="hidden md:flex flex-col gap-2 mt-auto">
                  {fulfillmentDisplay}
                  {collapsedResultsSummaryDisplay}
                </div>
              </div>
              <div className="button button--secondary md:hidden">
                view details
              </div>
            </div>
          </div>
          {/* Extra column hidden on mobile devices, added on tablets and larger */}
          <div className="hidden md:flex flex-col gap-2 mt-1">
            {variantInfo}
            <div className="button button--secondary mt-auto">view details</div>
          </div>
        </div>
      </Link>
    </div>
  );
};

const PriceDisplay = ({
  dealDiscountLabel,
  offerLabel,
  originalPrice,
  price,
  amount,
}: {
  amount?: string;
  dealActive: boolean;
  dealDiscountLabel?: string | null;
  offerLabel?: string | null;
  originalPrice: string | null;
  price: string;
}) => {
  return (
    <>
      {offerLabel && <OfferLabel label={offerLabel} />}

      {dealDiscountLabel && (
        <div>
          <span className="bg-notification rounded text-white text-xs mr-sm px-2 py-1">
            {dealDiscountLabel}
          </span>
        </div>
      )}

      <div className="flex gap-1 items-baseline">
        <div className="font-bold text-lg">{price}</div>
        <div className="text-xs">{amount}</div>
        {dealDiscountLabel && originalPrice && (
          <div className="text-xs ml-sm line-through">{originalPrice}</div>
        )}
      </div>
    </>
  );
};

export default ShopItemCard;
